import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Chapter } from '@simultrain/shared';
import { api } from '../api/client.js';

type Props = {
  chapter: Chapter;
  onClose: () => void;
  onSaved: (updated: Chapter) => void;
};

export function ChapterSettingsModal({ chapter, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(chapter.title);
  const [description, setDescription] = useState(chapter.description);
  const [minFirstAttemptPct, setMinFirstAttemptPct] = useState(
    Math.round(chapter.masteryThreshold.minFirstAttemptCorrect * 100),
  );
  const [maxSelfCorrected, setMaxSelfCorrected] = useState(chapter.masteryThreshold.maxSelfCorrectedMistakes);
  const [maxUncorrected, setMaxUncorrected] = useState(chapter.masteryThreshold.maxUncorrectedMistakes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.patchChapter(chapter.id, {
        title,
        description,
        masteryThreshold: {
          ...chapter.masteryThreshold,
          minFirstAttemptCorrect: Math.max(0, Math.min(100, minFirstAttemptPct)) / 100,
          maxSelfCorrectedMistakes: maxSelfCorrected,
          maxUncorrectedMistakes: maxUncorrected,
        },
      });
      onSaved(updated);
      onClose();
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 24,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 10, padding: 24,
        width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>{t('chapters.settings')}</h2>
          <button className="secondary" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div style={{ marginBottom: 8 }}>
          <label>{t('projects.name')}</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>{t('projects.description')}</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <hr style={{ margin: '16px 0', borderColor: 'var(--border)' }} />
        <h3 style={{ marginTop: 0 }}>{t('chapters.masterySection')}</h3>
        <p className="muted" style={{ fontSize: 13 }}>{t('chapters.masteryHint')}</p>

        <div style={{ marginBottom: 12 }}>
          <label>{t('chapters.minFirstAttemptCorrect')}</label>
          <input
            type="number" min={0} max={100} step={5}
            value={minFirstAttemptPct}
            onChange={(e) => setMinFirstAttemptPct(Number(e.target.value))}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>{t('chapters.maxSelfCorrectedMistakes')}</label>
          <input
            type="number" min={0} max={20}
            value={maxSelfCorrected}
            onChange={(e) => setMaxSelfCorrected(Number(e.target.value))}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>{t('chapters.maxUncorrectedMistakes')}</label>
          <input
            type="number" min={0} max={20}
            value={maxUncorrected}
            onChange={(e) => setMaxUncorrected(Number(e.target.value))}
          />
        </div>

        <button onClick={save} disabled={saving}>{t('steps.save')}</button>
      </div>
    </div>
  );
}
